/**
 * BuilderBuddi Calculator Formula Library
 * All formulas are deterministic, pure functions.
 * All values stored at full floating-point precision.
 * Only round for display — never for intermediate calculations.
 */

// ─── Core Pricing ───────────────────────────────────────────────────────────

/** Correct margin pricing: price = cost / (1 - margin%)
 * NEVER use: price = cost * (1 + markup%) — this systematically underprices */
export function applyMargin(jobCost: number, marginDecimal: number): number {
  if (marginDecimal >= 1 || marginDecimal < 0) throw new RangeError('Margin must be 0–0.99')
  return jobCost / (1 - marginDecimal)
}

/** True burden-loaded labour cost */
export function burdenedLabourRate(baseRate: number, onCostFactor: number): number {
  return Math.max(0, baseRate) * Math.max(0, onCostFactor)
}

/** Overhead cost for a specific job */
export function jobOverheadAllocation(
  monthlyOverhead: number,
  billableHoursPerMonth: number,
  jobHours: number
): number {
  if (billableHoursPerMonth <= 0 || jobHours <= 0 || monthlyOverhead <= 0) return 0
  return (monthlyOverhead / billableHoursPerMonth) * jobHours
}

// ─── Painting: Interior ─────────────────────────────────────────────────────

export interface Room {
  name: string
  length: number       // metres
  width: number        // metres
  height: number       // metres
  doors: number
  windows: number
  windowWidth: number  // metres
  windowHeight: number // metres
}

export interface PaintingInteriorInputs {
  rooms: Room[]
  coats: number
  coverageRatePerLitre: number    // m² per litre (default 10)
  primerCoveragePerLitre: number  // m² per litre primer (default 7)
  includeCeiling: boolean
  includePrimer: boolean
  labourRatePerHour: number       // burden-loaded
  paintPricePerLitre: number
  primerPricePerLitre: number
  complexityMultiplier: number    // 1.0 = standard
}

export interface PaintingInteriorResult {
  totalWallArea: number
  totalCeilingArea: number
  totalPaintableArea: number
  litresNeeded: number
  primerLitresNeeded: number
  labourHours: number
  materialsCost: number
  labourCost: number
  totalJobCost: number
}

const DOOR_AREA = 0.9 * 2.1   // 1.89 m²

export function calcPaintingInterior(inputs: PaintingInteriorInputs): PaintingInteriorResult {
  let totalWallArea = 0
  let totalCeilingArea = 0

  for (const room of inputs.rooms) {
    const wallArea =
      2 * (room.length + room.width) * room.height
      - (room.doors * DOOR_AREA)
      - (room.windows * room.windowWidth * room.windowHeight)
    totalWallArea += Math.max(0, wallArea)
    if (inputs.includeCeiling) {
      totalCeilingArea += room.length * room.width
    }
  }

  const totalPaintableArea = totalWallArea + totalCeilingArea
  const coats = Math.max(0, inputs.coats)
  const litresRaw = inputs.coverageRatePerLitre > 0
    ? (totalPaintableArea * coats) / inputs.coverageRatePerLitre * 1.10  // 10% waste
    : 0
  const litresNeeded = Math.ceil(litresRaw)
  const primerLitresNeeded = inputs.includePrimer && inputs.primerCoveragePerLitre > 0
    ? Math.ceil(totalPaintableArea / inputs.primerCoveragePerLitre * 1.10)
    : 0

  // Labour: walls 15m²/hr roller, ceilings 18m²/hr, x complexity
  const wallLabourHours = (totalWallArea / 15) * coats
  const ceilingLabourHours = (totalCeilingArea / 18) * coats
  const prepHours = (wallLabourHours + ceilingLabourHours) * 0.25
  const labourHours = (wallLabourHours + ceilingLabourHours + prepHours) * inputs.complexityMultiplier

  const paintCost = litresNeeded * inputs.paintPricePerLitre
  const primerCost = primerLitresNeeded * inputs.primerPricePerLitre
  const materialsCost = paintCost + primerCost
  const labourCost = labourHours * Math.max(0, inputs.labourRatePerHour)
  const totalJobCost = materialsCost + labourCost

  return {
    totalWallArea,
    totalCeilingArea,
    totalPaintableArea,
    litresNeeded,
    primerLitresNeeded,
    labourHours,
    materialsCost,
    labourCost,
    totalJobCost,
  }
}

// ─── Painting: Exterior ─────────────────────────────────────────────────────

export interface PaintingExteriorInputs {
  perimeter: number         // metres
  wallHeight: number        // metres
  numDoors: number
  numWindows: number
  windowWidth: number
  windowHeight: number
  numGableEnds: number
  gableWidth: number
  gableHeight: number
  coats: number
  coverageRatePerLitre: number
  paintPricePerLitre: number
  labourRatePerHour: number
  complexityMultiplier: number
}

export function calcPaintingExterior(inputs: PaintingExteriorInputs) {
  const facadeArea = inputs.perimeter * inputs.wallHeight
    - (inputs.numDoors * DOOR_AREA)
    - (inputs.numWindows * inputs.windowWidth * inputs.windowHeight)
  const gableArea = inputs.numGableEnds * 0.5 * inputs.gableWidth * inputs.gableHeight
  const totalArea = Math.max(0, facadeArea) + gableArea

  const coats = Math.max(0, inputs.coats)
  const coverage = inputs.coverageRatePerLitre > 0 ? inputs.coverageRatePerLitre : Number.POSITIVE_INFINITY
  const litresNeeded = Math.ceil(totalArea * coats / coverage * 1.15)
  const labourHours = (totalArea / 12) * coats * Math.max(0, inputs.complexityMultiplier)
  const materialsCost = litresNeeded * Math.max(0, inputs.paintPricePerLitre)
  const labourCost = labourHours * Math.max(0, inputs.labourRatePerHour)

  return { totalArea, litresNeeded, labourHours, materialsCost, labourCost, totalJobCost: materialsCost + labourCost }
}

// ─── Paint Quantity Estimator ───────────────────────────────────────────────

export function calcPaintQuantity(areaSqm: number, coats: number, coveragePerLitre: number, wasteFactor = 1.10) {
  const safeCoverage = coveragePerLitre > 0 ? coveragePerLitre : Number.POSITIVE_INFINITY
  const litresRaw = (Math.max(0, areaSqm) * Math.max(0, coats)) / safeCoverage * Math.max(1, wasteFactor)
  return { litresRaw, litresNeeded: Math.ceil(litresRaw) }
}

// ─── Concrete: Slab ─────────────────────────────────────────────────────────

export interface ConcreteSlabInputs {
  length: number
  width: number
  thicknessMm: number
  wasteFactor: number           // 1.07 = 7%
  concreteM3Price: number
  labourRatePerHour: number
  reinforcingKgPerM2: number    // 3–5 kg/m² for slabs
  steelPricePerKg: number
  finishType: 'broom' | 'float' | 'polish' | 'exposed_aggregate'
}

export function calcConcreteSlab(inputs: ConcreteSlabInputs) {
  const areaM2 = Math.max(0, inputs.length) * Math.max(0, inputs.width)
  const volumeRaw = areaM2 * (Math.max(0, inputs.thicknessMm) / 1000)
  const volumeWithWaste = volumeRaw * Math.max(1, inputs.wasteFactor)
  const volumeOrder = Math.ceil(volumeWithWaste * 2) / 2  // round up to 0.5m³

  const concreteM3Cost = volumeOrder * Math.max(0, inputs.concreteM3Price)

  // Labour
  const pourLabourHours = volumeWithWaste * 1.0
  const formworkHours = (2 * (inputs.length + inputs.width)) * 0.75
  const finishMultiplier = inputs.finishType === 'polish' ? 2.0 : inputs.finishType === 'exposed_aggregate' ? 1.5 : inputs.finishType === 'float' ? 1.0 : 0.8
  const finishHours = areaM2 * 0.2 * finishMultiplier
  const totalLabourHours = pourLabourHours + formworkHours + finishHours

  // Reinforcing
  const steelKg = areaM2 * Math.max(0, inputs.reinforcingKgPerM2)
  const steelCost = steelKg * Math.max(0, inputs.steelPricePerKg)

  const labourCost = totalLabourHours * Math.max(0, inputs.labourRatePerHour)
  const totalJobCost = concreteM3Cost + steelCost + labourCost

  return { areaM2, volumeRaw, volumeOrder, concreteM3Cost, steelKg, steelCost, totalLabourHours, labourCost, totalJobCost }
}

// ─── Concrete: Footings ─────────────────────────────────────────────────────

export function calcConcreteFootings(length: number, width: number, depth: number, wasteFactor: number, pricePerM3: number) {
  const volumeRaw = Math.max(0, length) * Math.max(0, width) * Math.max(0, depth)
  const volumeWithWaste = volumeRaw * Math.max(1, wasteFactor)
  const volumeOrder = Math.ceil(volumeWithWaste * 2) / 2
  return { volumeRaw, volumeWithWaste, volumeOrder, cost: volumeOrder * Math.max(0, pricePerM3) }
}

// ─── Tiling: Full ───────────────────────────────────────────────────────────

export interface TilingInputs {
  areaSqm: number
  tileLengthMm: number
  tileWidthMm: number
  groutJointMm: number
  layPattern: 'straight' | 'diagonal' | 'herringbone' | 'complex'
  adhesiveKgPerM2: number
  adhesiveBagKg: number
  adhesivePricePerBag: number
  tilePricePerM2: number
  labourRatePerHour: number
}

export function calcTiling(inputs: TilingInputs) {
  const wasteFactor = { straight: 1.10, diagonal: 1.15, herringbone: 1.15, complex: 1.20 }[inputs.layPattern]

  const tileAreaM2 = (Math.max(1, inputs.tileLengthMm) / 1000) * (Math.max(1, inputs.tileWidthMm) / 1000)
  const areaSqm = Math.max(0, inputs.areaSqm)
  const tilesNeeded = Math.ceil(areaSqm / tileAreaM2 * wasteFactor)
  const tileCost = areaSqm * wasteFactor * Math.max(0, inputs.tilePricePerM2)

  // Adhesive
  const adhesiveKgNeeded = areaSqm * Math.max(0, inputs.adhesiveKgPerM2) * 1.12
  const adhesiveBagKg = Math.max(1, inputs.adhesiveBagKg)
  const adhesiveBagsNeeded = Math.ceil(adhesiveKgNeeded / adhesiveBagKg)
  const adhesiveCost = adhesiveBagsNeeded * Math.max(0, inputs.adhesivePricePerBag)

  // Grout
  const tileRatio = (inputs.tileLengthMm * inputs.tileWidthMm) /
    ((inputs.tileLengthMm + inputs.groutJointMm) * (inputs.tileWidthMm + inputs.groutJointMm))
  const groutVolumeM3 = areaSqm * (1 - tileRatio) * (Math.max(0, inputs.groutJointMm) / 1000)
  const groutKg = groutVolumeM3 * 1800 * 1.10

  // Labour — rate drops for complex patterns due to more cuts and alignment work
  const layRateByPattern: Record<typeof inputs.layPattern, number> = {
    straight: 4.0,    // m²/hr
    diagonal: 3.2,    // ~20% slower — more cuts, 45° alignment
    herringbone: 2.8, // ~30% slower — precise interlocking cuts
    complex: 2.0,     // 50% slower — bespoke layouts
  }
  const layRate = layRateByPattern[inputs.layPattern]
  const layHours = areaSqm / layRate
  const groutHours = layHours * 0.35
  const totalLabourHours = layHours + groutHours
  const labourCost = totalLabourHours * Math.max(0, inputs.labourRatePerHour)

  const totalJobCost = tileCost + adhesiveCost + labourCost

  return { tilesNeeded, tileCost, adhesiveBagsNeeded, adhesiveKgNeeded, adhesiveCost, groutKg, totalLabourHours, labourCost, totalJobCost }
}

// ─── Roofing Material ───────────────────────────────────────────────────────

export function calcRoofing(planAreaM2: number, pitchDeg: number, tileUnitCoverageM2: number, tilePricePerUnit: number) {
  const pitchCorrection = 1 / Math.cos(Math.max(0, pitchDeg) * Math.PI / 180)
  const roofArea = Math.max(0, planAreaM2) * pitchCorrection
  const unitCoverage = tileUnitCoverageM2 > 0 ? tileUnitCoverageM2 : Number.POSITIVE_INFINITY
  const unitsNeeded = Math.ceil(roofArea / unitCoverage * 1.10)
  return { roofArea, unitsNeeded, materialCost: unitsNeeded * Math.max(0, tilePricePerUnit) }
}

// ─── Paving + Sub-base ──────────────────────────────────────────────────────

export function calcPaving(
  areaSqm: number,
  paverAreaM2: number,
  paverPriceEach: number,
  subbaseDepthMm: number,
  subbasePricePerTon: number,
  sandBeddingDepthMm: number,
  sandPricePerTon: number,
) {
  const safeArea = Math.max(0, areaSqm)
  const safePaverArea = paverAreaM2 > 0 ? paverAreaM2 : Number.POSITIVE_INFINITY
  const pavesNeeded = Math.ceil(safeArea / safePaverArea * 1.10)
  const paverCost = pavesNeeded * Math.max(0, paverPriceEach)
  const subbaseVolumeM3 = safeArea * Math.max(0, subbaseDepthMm) / 1000
  const subbaseTons = subbaseVolumeM3 * 1.7
  const subbaseCost = subbaseTons * Math.max(0, subbasePricePerTon)
  const sandVolumeM3 = safeArea * Math.max(0, sandBeddingDepthMm) / 1000
  const sandTons = sandVolumeM3 * 1.6
  const sandCost = sandTons * Math.max(0, sandPricePerTon)
  return { pavesNeeded, paverCost, subbaseTons, subbaseCost, sandTons, sandCost, totalMaterialsCost: paverCost + subbaseCost + sandCost }
}

// ─── Plastering ─────────────────────────────────────────────────────────────

export function calcPlastering(areaSqm: number, renderThicknessMm: number, topcoatThicknessMm: number, bagWeightKg: number, bagPrice: number, labourRatePerHour: number) {
  const safeArea = Math.max(0, areaSqm)
  const renderVolumeM3 = safeArea * Math.max(0, renderThicknessMm) / 1000
  const topcoatVolumeM3 = safeArea * Math.max(0, topcoatThicknessMm) / 1000
  const safeBagWeight = Math.max(1, bagWeightKg)
  const totalBags = Math.ceil((renderVolumeM3 + topcoatVolumeM3) * 1700 / safeBagWeight * 1.10)
  const materialsCost = totalBags * Math.max(0, bagPrice)
  const labourHours = safeArea / 12  // ~12m²/day ÷ 8hr = 1.5m²/hr
  const labourCost = labourHours * Math.max(0, labourRatePerHour)
  return { totalBags, materialsCost, labourHours, labourCost, totalJobCost: materialsCost + labourCost }
}

// ─── Waterproofing ──────────────────────────────────────────────────────────

export function calcWaterproofing(areaSqm: number, coats: number, coverageRateM2PerKg: number, productPricePerKg: number, labourRatePerHour: number) {
  const safeArea = Math.max(0, areaSqm)
  const safeCoats = Math.max(0, coats)
  const safeCoverage = coverageRateM2PerKg > 0 ? coverageRateM2PerKg : Number.POSITIVE_INFINITY
  const kgNeeded = Math.ceil(safeArea / safeCoverage * safeCoats * 1.15)
  const materialsCost = kgNeeded * Math.max(0, productPricePerKg)
  const labourHours = safeArea * 0.15 * safeCoats
  const labourCost = labourHours * Math.max(0, labourRatePerHour)
  return { kgNeeded, materialsCost, labourHours, labourCost, totalJobCost: materialsCost + labourCost }
}

// ─── Area Calculator (multi-shape) ──────────────────────────────────────────

export type ShapeType = 'rectangle' | 'triangle' | 'circle' | 'trapezoid'

export interface Shape {
  type: ShapeType
  label: string
  a: number   // length / base / radius / parallel-a
  b?: number  // width / height / parallel-b
  h?: number  // height (trapezoid)
}

export function calcShapeArea(shape: Shape): number {
  switch (shape.type) {
    case 'rectangle': return Math.max(0, shape.a) * Math.max(0, shape.b ?? 0)
    case 'triangle': return 0.5 * Math.max(0, shape.a) * Math.max(0, shape.b ?? 0)
    case 'circle': return Math.PI * Math.max(0, shape.a) * Math.max(0, shape.a)
    case 'trapezoid': return 0.5 * (Math.max(0, shape.a) + Math.max(0, shape.b ?? 0)) * Math.max(0, shape.h ?? 0)
    default: return 0
  }
}

export function calcTotalArea(shapes: Shape[]): number {
  return shapes.reduce((sum, s) => sum + calcShapeArea(s), 0)
}

// ─── True Labour Rate ───────────────────────────────────────────────────────

export function calcTrueLabourRate(baseRate: number, onCostFactor: number) {
  const trueRate = Math.max(0, baseRate) * Math.max(0, onCostFactor)
  return { trueRate, onCostAmount: trueRate - baseRate }
}

// ─── Overhead Allocator ─────────────────────────────────────────────────────

export function calcOverheadAllocator(monthlyOverhead: number, billableHoursPerMonth: number, jobHours: number) {
  const hourlyRate = billableHoursPerMonth > 0 ? monthlyOverhead / billableHoursPerMonth : 0
  return { hourlyRate, jobAllocation: hourlyRate * jobHours }
}

// ─── Markup vs Margin (educational) ─────────────────────────────────────────

export function calcMarkupVsMargin(cost: number, percent: number) {
  const safePercent = Math.max(0, Math.min(percent, 99.99))
  const safeCost = Math.max(0, cost)
  const markupPrice = safeCost * (1 + safePercent / 100)
  const actualMarkupMargin = (markupPrice - cost) / markupPrice * 100
  const marginPrice = safeCost / (1 - safePercent / 100)
  const actualMarginMargin = (marginPrice - cost) / marginPrice * 100
  return {
    markupPrice,
    actualMarkupMargin,  // will be less than the desired percent
    marginPrice,
    actualMarginMargin,  // will equal the desired percent
    difference: marginPrice - markupPrice,
  }
}

// ─── Deposit Calculator ─────────────────────────────────────────────────────

export function calcDeposit(totalIncVat: number, depositPercent: number, milestones: number[] = []) {
  const safeTotal = Math.max(0, totalIncVat)
  const safeDepositPercent = Math.max(0, Math.min(100, depositPercent))
  const deposit = safeTotal * (safeDepositPercent / 100)
  const balanceAfterDeposit = safeTotal - deposit
  const milestoneAmounts = milestones.map((m) => safeTotal * (Math.max(0, Math.min(100, m)) / 100))
  return { deposit, balanceAfterDeposit, milestoneAmounts }
}

// ─── VAT Calculator ─────────────────────────────────────────────────────────

export function calcVat(amount: number, vatRate: number, mode: 'add' | 'extract') {
  const safeAmount = Math.max(0, amount)
  const safeVatRate = Math.max(0, vatRate)
  if (mode === 'add') {
    return { exclAmount: safeAmount, vatAmount: safeAmount * safeVatRate, inclAmount: safeAmount * (1 + safeVatRate) }
  } else {
    const exclAmount = safeAmount / (1 + safeVatRate)
    const vatAmount = safeAmount - exclAmount
    return { exclAmount, vatAmount, inclAmount: safeAmount }
  }
}

// ─── Wastage & Ordering ─────────────────────────────────────────────────────

export function calcWastageOrdering(requiredQty: number, wasteFactor: number, packSize: number, unitPrice: number) {
  const safeRequiredQty = Math.max(0, requiredQty)
  const safeWasteFactor = Math.max(1, wasteFactor)
  const safePackSize = Math.max(1, packSize)
  const safeUnitPrice = Math.max(0, unitPrice)
  const orderQty = Math.ceil(safeRequiredQty * safeWasteFactor / safePackSize) * safePackSize
  const leftover = orderQty - safeRequiredQty * safeWasteFactor
  const cost = orderQty / safePackSize * safeUnitPrice
  return { orderQty, leftover, cost }
}

// ─── Day Rate vs Project Rate ────────────────────────────────────────────────

export function calcDayRateVsProject(projectPrice: number, estimatedDays: number, dayRate: number) {
  const safeDays = Math.max(0, estimatedDays)
  const safeProjectPrice = Math.max(0, projectPrice)
  const safeDayRate = Math.max(0, dayRate)
  const dayRateEquivalent = safeDays > 0 ? safeProjectPrice / safeDays : 0
  const dayRateTotal = safeDayRate * safeDays
  const threshold = dayRateTotal * 0.80
  const isUnderpriced = safeProjectPrice < threshold
  return { dayRateEquivalent, dayRateTotal, threshold, isUnderpriced, difference: safeProjectPrice - dayRateTotal }
}

// ─── Quote Conversion Forecast ───────────────────────────────────────────────

export function calcQuoteConversion(quotesPerMonth: number, avgQuoteValue: number, conversionRatePercent: number) {
  const safeQuotesPerMonth = Math.max(0, quotesPerMonth)
  const safeAvgQuoteValue = Math.max(0, avgQuoteValue)
  const safeConversionRate = Math.max(0, Math.min(100, conversionRatePercent))
  const acceptedPerMonth = safeQuotesPerMonth * (safeConversionRate / 100)
  const monthlyRevenueForecast = acceptedPerMonth * safeAvgQuoteValue
  const annualForecast = monthlyRevenueForecast * 12
  return {
    acceptedPerMonth,
    // Backward-compatible field name used by current UI.
    wonPerMonth: acceptedPerMonth,
    monthlyRevenueForecast,
    annualForecast,
  }
}

// ─── Break-Even ──────────────────────────────────────────────────────────────

export function calcBreakEven(fixedCosts: number, avgJobValue: number, variableCostPercent: number) {
  const safeFixedCosts = Math.max(0, fixedCosts)
  const safeAvgJobValue = Math.max(0, avgJobValue)
  const safeVariableCostPercent = Math.max(0, Math.min(100, variableCostPercent))
  const contributionMargin = 1 - safeVariableCostPercent / 100
  const breakEvenRevenue = contributionMargin > 0 ? safeFixedCosts / contributionMargin : 0
  const breakEvenJobs = safeAvgJobValue > 0 ? Math.ceil(breakEvenRevenue / safeAvgJobValue) : 0
  return { breakEvenRevenue, breakEvenJobs }
}

// ─── Job Profitability ────────────────────────────────────────────────────────

export function calcJobProfitability(quotedTotal: number, quotedCost: number, actualTotal: number, actualCost: number) {
  const safeQuotedTotal = Math.max(0, quotedTotal)
  const safeActualTotal = Math.max(0, actualTotal)
  const quotedMargin = safeQuotedTotal > 0 ? (safeQuotedTotal - quotedCost) / safeQuotedTotal * 100 : 0
  const actualMargin = safeActualTotal > 0 ? (safeActualTotal - actualCost) / safeActualTotal * 100 : 0
  const variance = actualMargin - quotedMargin
  const revenueDiff = safeActualTotal - safeQuotedTotal
  const costDiff = actualCost - quotedCost
  return { quotedMargin, actualMargin, variance, revenueDiff, costDiff }
}

// ─── Transport Cost ───────────────────────────────────────────────────────────

export function calcTransport(distanceKm: number, fuelRatePerKm: number, labourRatePerHour: number, avgSpeedKph: number, billTravelTime: boolean) {
  const safeDistanceKm = Math.max(0, distanceKm)
  const safeFuelRate = Math.max(0, fuelRatePerKm)
  const safeLabourRate = Math.max(0, labourRatePerHour)
  const safeSpeed = avgSpeedKph > 0 ? avgSpeedKph : Number.POSITIVE_INFINITY
  const fuelCost = safeDistanceKm * 2 * safeFuelRate  // return trip
  const travelTimeHours = (safeDistanceKm * 2) / safeSpeed
  const travelLabourCost = billTravelTime ? travelTimeHours * safeLabourRate : 0
  return { fuelCost, travelTimeHours, travelLabourCost, totalTransportCost: fuelCost + travelLabourCost }
}

// ─── Voltage Drop + Cable Size ────────────────────────────────────────────────

const CABLE_SIZES_MM2 = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120]

export function calcVoltageDrop(currentAmps: number, lengthM: number, voltageV: number, material: 'copper' | 'aluminium', allowedDropPct: number) {
  const rho = material === 'copper' ? 0.0175 : 0.028  // Ω·mm²/m at 75°C approx
  const safeI = Math.max(0, currentAmps)
  const safeL = Math.max(0, lengthM)
  const safeV = voltageV > 0 ? voltageV : 230
  const allowedDropV = (Math.max(0, allowedDropPct) / 100) * safeV
  const sizeResults = CABLE_SIZES_MM2.map((areaMm2) => {
    const dropV = 2 * safeI * rho * safeL / areaMm2  // factor 2 for return path
    return { areaMm2, dropV, dropPct: (dropV / safeV) * 100, resistance: 2 * rho * safeL / areaMm2 }
  })
  const recommended = sizeResults.find((s) => s.dropV <= allowedDropV) ?? sizeResults[sizeResults.length - 1]
  return {
    recommendedMm2: recommended.areaMm2,
    dropVolts: recommended.dropV,
    dropPct: recommended.dropPct,
    compliant: recommended.dropV <= allowedDropV,
    allowedDropV,
    resistance: recommended.resistance,
    sizeResults,
  }
}

// ─── Conduit Fill ─────────────────────────────────────────────────────────────

// Nominal ID in mm for standard conduit sizes (20mm, 25mm, 32mm, 40mm, 50mm, 63mm)
const CONDUIT_ID_MM: Record<number, number> = { 16: 12.0, 20: 15.8, 25: 21.2, 32: 27.0, 40: 35.0, 50: 43.0, 63: 56.0 }

export function calcConduitFill(conduitSizeMm: number, wireCount: number, wireDiameterMm: number) {
  const idMm = CONDUIT_ID_MM[conduitSizeMm] ?? conduitSizeMm * 0.79
  const conduitAreaMm2 = Math.PI * (idMm / 2) ** 2
  const singleWireArea = Math.PI * (Math.max(0, wireDiameterMm) / 2) ** 2
  const totalWireArea = Math.max(0, wireCount) * singleWireArea
  const fillPct = conduitAreaMm2 > 0 ? (totalWireArea / conduitAreaMm2) * 100 : 0
  const maxFillPct = wireCount <= 1 ? 53 : wireCount === 2 ? 31 : 40  // SANS 10142-1
  const compliant = fillPct <= maxFillPct
  // Find smallest compliant conduit
  const sortedSizes = Object.keys(CONDUIT_ID_MM).map(Number).sort((a, b) => a - b)
  const minConduit = sortedSizes.find((sz) => {
    const id = CONDUIT_ID_MM[sz]!
    const area = Math.PI * (id / 2) ** 2
    const fill = totalWireArea / area * 100
    const limit = wireCount <= 1 ? 53 : wireCount === 2 ? 31 : 40
    return fill <= limit
  })
  return { conduitAreaMm2, totalWireArea, fillPct, maxFillPct, compliant, minConduitMm: minConduit ?? null }
}

// ─── Water Supply Pipe Sizing ─────────────────────────────────────────────────

// Typical residential flow rates (L/s) per fixture: 0.1–0.6
export function calcWaterSupplyPipe(flowLps: number, pipeLengthM: number, material: 'copper' | 'pvc' | 'cpvc' | 'galv') {
  const safeQ = Math.max(0, flowLps)  // L/s
  const Q_m3s = safeQ / 1000
  // Hazen-Williams C factor by material
  const C: Record<string, number> = { copper: 130, pvc: 150, cpvc: 130, galv: 100 }
  const hwC = C[material] ?? 130
  // Standard nominal sizes in mm (ID approx)
  const pipeSizes = [
    { dn: 15, id: 13.4 }, { dn: 20, id: 18.9 }, { dn: 25, id: 24.5 },
    { dn: 32, id: 31.8 }, { dn: 40, id: 39.6 }, { dn: 50, id: 49.4 },
  ]
  // Find minimum size where velocity ≤ 2.5 m/s (SANS 10252 residential)
  const sizeResults = pipeSizes.map(({ dn, id }) => {
    const area = Math.PI * (id / 1000 / 2) ** 2
    const velocity = area > 0 ? Q_m3s / area : 0
    // Darcy-Weisbach simplified via H-W: hf = 10.67 * L * Q^1.852 / (C^1.852 * D^4.87)
    const headLoss = pipeLengthM > 0
      ? 10.67 * pipeLengthM * (safeQ / 1000) ** 1.852 / (hwC ** 1.852 * (id / 1000) ** 4.87)
      : 0
    const pressureDropKpa = headLoss * 9.81
    return { dn, id, velocity, headLoss, pressureDropKpa, compliant: velocity <= 2.5 && velocity >= 0.3 }
  })
  const recommended = sizeResults.find((s) => s.compliant) ?? sizeResults[sizeResults.length - 1]
  return { recommendedDn: recommended.dn, velocity: recommended.velocity, pressureDropKpa: recommended.pressureDropKpa, sizeResults }
}

// ─── Drain Pipe Sizing ────────────────────────────────────────────────────────

// Drainage Fixture Units → flow rate (L/s) per AS/SANS 10252
export function calcDrainPipe(fixtureUnits: number, slopePct: number) {
  const dfu = Math.max(1, fixtureUnits)
  const slope = Math.max(0.5, Math.min(10, slopePct)) / 100
  // Approx: Q (L/s) = 0.33 * DFU^0.5 (Malan formula, SANS 10252)
  const flowLps = 0.33 * Math.sqrt(dfu)
  // Manning's n for PVC drain: 0.011
  const n = 0.011
  // Full-pipe Manning capacity Q = (1/n) * A * R^(2/3) * S^(1/2)
  // Find minimum DN where Q_capacity ≥ flowLps at 0.8 full flow
  const pipeSizes = [
    { dn: 40 }, { dn: 50 }, { dn: 75 }, { dn: 100 }, { dn: 150 }, { dn: 200 },
  ]
  const sizeResults = pipeSizes.map(({ dn }) => {
    const r = dn / 1000 / 2
    const area = Math.PI * r * r
    const hydraulicR = r / 2
    const qFull = (1 / n) * area * hydraulicR ** (2 / 3) * Math.sqrt(slope) * 1000  // L/s
    const q80 = qFull * 0.8
    return { dn, qFull, q80Lps: q80, compliant: q80 >= flowLps }
  })
  const recommended = sizeResults.find((s) => s.compliant) ?? sizeResults[sizeResults.length - 1]
  const velocity = recommended.q80Lps / (Math.PI * ((recommended.dn / 1000) / 2) ** 2) / 1000
  return { designFlowLps: flowLps, recommendedDn: recommended.dn, capacity80Lps: recommended.q80Lps, velocity, sizeResults }
}

// ─── Countertop Area ──────────────────────────────────────────────────────────

export function calcCountertopArea(
  sections: Array<{ length: number; width: number }>,
  overhangM: number,
  cutouts: Array<{ length: number; width: number }>,
  wastePct: number,
  pricePerM2: number,
) {
  const grossArea = sections.reduce((sum, s) => sum + Math.max(0, s.length) * Math.max(0, s.width), 0)
  const overhangArea = sections.reduce((sum, s) => sum + Math.max(0, s.length) * Math.max(0, overhangM), 0)
  const cutoutArea = cutouts.reduce((sum, c) => sum + Math.max(0, c.length) * Math.max(0, c.width), 0)
  const netArea = Math.max(0, grossArea + overhangArea - cutoutArea)
  const orderArea = netArea * (1 + Math.max(0, wastePct) / 100)
  const materialCost = orderArea * Math.max(0, pricePerM2)
  return { grossArea, overhangArea, cutoutArea, netArea, orderArea, materialCost }
}

// ─── Asphalt Driveway ─────────────────────────────────────────────────────────

export function calcAsphaltDriveway(
  lengthM: number, widthM: number, asphaltThicknessMm: number,
  baseThicknessMm: number, asphaltPricePerTonne: number, basePricePerTonne: number,
  labourRatePerM2: number, wastePct: number,
) {
  const area = Math.max(0, lengthM) * Math.max(0, widthM)
  const wasteFactor = 1 + Math.max(0, wastePct) / 100
  // Asphalt density ~2.4 t/m³; base (crusher run) ~2.1 t/m³
  const asphaltVolume = area * (Math.max(0, asphaltThicknessMm) / 1000) * wasteFactor
  const baseVolume = area * (Math.max(0, baseThicknessMm) / 1000) * wasteFactor
  const asphaltTonnes = asphaltVolume * 2.4
  const baseTonnes = baseVolume * 2.1
  const asphaltCost = asphaltTonnes * Math.max(0, asphaltPricePerTonne)
  const baseCost = baseTonnes * Math.max(0, basePricePerTonne)
  const labourCost = area * Math.max(0, labourRatePerM2)
  const totalCost = asphaltCost + baseCost + labourCost
  return { areaM2: area, asphaltTonnes, baseTonnes, asphaltCost, baseCost, labourCost, totalCost }
}

// ─── Window Cleaning ──────────────────────────────────────────────────────────

const WINDOW_SIZE_M2: Record<string, number> = { small: 0.5, medium: 1.2, large: 2.4, xlarge: 4.0 }
const WINDOW_TIME_MIN: Record<string, number> = { small: 5, medium: 10, large: 18, xlarge: 30 }

export function calcWindowCleaning(
  windows: Array<{ size: 'small' | 'medium' | 'large' | 'xlarge'; count: number; stories: number }>,
  labourRatePerHour: number, pricePerM2: number,
) {
  let totalAreaM2 = 0
  let totalTimeMin = 0
  let totalPanes = 0
  for (const w of windows) {
    const areaEach = WINDOW_SIZE_M2[w.size] ?? 1.2
    const timeEach = WINDOW_TIME_MIN[w.size] ?? 10
    const storyMultiplier = 1 + (Math.max(1, w.stories) - 1) * 0.4  // 40% extra per additional storey
    totalAreaM2 += areaEach * w.count
    totalTimeMin += timeEach * w.count * storyMultiplier
    totalPanes += w.count
  }
  const labourHours = totalTimeMin / 60
  const labourCost = labourHours * Math.max(0, labourRatePerHour)
  const areaCost = totalAreaM2 * Math.max(0, pricePerM2)
  const suggestedPrice = Math.max(labourCost, areaCost)
  return { totalAreaM2, totalPanes, labourHours, labourCost, areaCost, suggestedPrice }
}

// ─── Timber Beam Sizing ───────────────────────────────────────────────────────

// fb = allowable bending stress (MPa), E = elastic modulus (GPa)
const TIMBER_SPECIES: Record<string, { fb: number; E: number; label: string }> = {
  sa_pine_s5: { fb: 7.5, E: 7.5, label: 'SA Pine (S5)' },
  sa_pine_s7: { fb: 10, E: 9.0, label: 'SA Pine (S7)' },
  eucalyptus: { fb: 18, E: 13, label: 'Eucalyptus' },
  saligna:    { fb: 20, E: 14, label: 'Saligna' },
  iroko:      { fb: 16, E: 11, label: 'Iroko / Hardwood' },
}

// Standard structural timber sections: [width_mm, depth_mm]
const TIMBER_SECTIONS: [number, number][] = [
  [38, 76], [38, 114], [38, 152], [38, 228], [50, 152], [50, 228],
  [76, 152], [76, 228], [76, 304], [114, 228], [114, 304],
]

export function calcTimberBeam(
  spanM: number, loadKnPerM: number, species: string, loadType: 'udl' | 'point_centre',
) {
  const sp = TIMBER_SPECIES[species] ?? TIMBER_SPECIES['sa_pine_s5']!
  const L = Math.max(0, spanM)
  const w = Math.max(0, loadKnPerM) * 1000  // N/m or N total
  // Bending moment (N·m): UDL = wL²/8, point centre = PL/4
  const M_Nm = loadType === 'udl' ? w * L * L / 8 : w * L / 4
  const M_Nmm = M_Nm * 1000
  // Required section modulus S = M / fb (fb in MPa = N/mm²)
  const requiredS_mm3 = sp.fb > 0 ? M_Nmm / sp.fb : 0
  // Find smallest section with S_actual ≥ requiredS
  const sectionResults = TIMBER_SECTIONS.map(([b, d]) => {
    const S = (b * d * d) / 6
    const I = (b * d * d * d) / 12
    const deflection_mm = loadType === 'udl'
      ? (5 * w * (L * 1000) ** 4) / (384 * sp.E * 1000 * I)
      : (w * (L * 1000) ** 3) / (48 * sp.E * 1000 * I)
    return { b, d, S, I, deflection_mm, adequate: S >= requiredS_mm3 && deflection_mm <= L * 1000 / 300 }
  })
  const recommended = sectionResults.find((s) => s.adequate) ?? sectionResults[sectionResults.length - 1]
  return {
    momentKnm: M_Nm / 1000,
    requiredS_mm3,
    species: sp.label,
    recommendedB: recommended.b,
    recommendedD: recommended.d,
    deflectionMm: recommended.deflection_mm,
    sectionAdequate: recommended.adequate,
  }
}

// ─── Circuit / Breaker Sizing ─────────────────────────────────────────────────

const STANDARD_BREAKERS = [6, 10, 16, 20, 25, 32, 40, 50, 63, 80, 100]
const CABLE_CURRENT_RATINGS: Array<{ areaMm2: number; ratingA: number }> = [
  { areaMm2: 1.5, ratingA: 15 }, { areaMm2: 2.5, ratingA: 20 }, { areaMm2: 4, ratingA: 27 },
  { areaMm2: 6, ratingA: 34 }, { areaMm2: 10, ratingA: 46 }, { areaMm2: 16, ratingA: 61 },
  { areaMm2: 25, ratingA: 80 }, { areaMm2: 35, ratingA: 99 }, { areaMm2: 50, ratingA: 119 },
]

export function calcCircuitBreaker(
  loadWatts: number, voltageV: number, phases: 1 | 3, powerFactor: number, demandFactor: number,
) {
  const safeV = voltageV > 0 ? voltageV : 230
  const safePF = Math.max(0.1, Math.min(1, powerFactor))
  const safeDF = Math.max(0.1, Math.min(1, demandFactor))
  const designLoad = Math.max(0, loadWatts) * safeDF
  const fullLoadAmps = phases === 3
    ? designLoad / (Math.sqrt(3) * safeV * safePF)
    : designLoad / (safeV * safePF)
  const designAmps = fullLoadAmps * 1.25  // NRS 097 / SANS 10142 125% rule
  const breakerA = STANDARD_BREAKERS.find((b) => b >= designAmps) ?? STANDARD_BREAKERS[STANDARD_BREAKERS.length - 1]!
  const cable = CABLE_CURRENT_RATINGS.find((c) => c.ratingA >= breakerA)
  return {
    fullLoadAmps,
    designAmps,
    breakerA,
    cableMm2: cable?.areaMm2 ?? 50,
    cableRatingA: cable?.ratingA ?? 119,
  }
}

// ─── Water Heater Sizing ──────────────────────────────────────────────────────

export function calcWaterHeater(
  occupants: number, usageType: 'shower' | 'bath' | 'mixed', heaterType: 'storage' | 'heat_pump' | 'solar',
  inletTempC: number, outletTempC: number,
) {
  const safeOcc = Math.max(1, Math.round(occupants))
  // Litres per person per day
  const lpd: Record<string, number> = { shower: 50, bath: 90, mixed: 65 }
  const litresPerPersonPerDay = lpd[usageType] ?? 65
  const totalLitresPerDay = safeOcc * litresPerPersonPerDay
  // Peak demand factor: storage tank should handle 70% of daily demand in one hit
  const peakLitres = totalLitresPerDay * 0.7
  // Round up to standard geyser sizes
  const geysersizes = [50, 100, 150, 200, 250, 300, 400]
  const recommendedCapacity = geysersizes.find((s) => s >= peakLitres) ?? geysersizes[geysersizes.length - 1]!
  // Element sizing: Q = m·Cp·ΔT / t (t = 1 hr recovery)
  const deltaT = Math.max(1, (outletTempC || 60) - (inletTempC || 15))
  const massKg = recommendedCapacity  // water ≈ 1 kg/L
  const elementKw = (massKg * 4.186 * deltaT) / 3600  // kJ = kW·s
  const efficiencyFactor = heaterType === 'heat_pump' ? 3.5 : heaterType === 'solar' ? 2.5 : 1.0
  const effectiveKw = elementKw / efficiencyFactor
  const standardElements = [1.5, 2, 3, 4, 6]
  const elementSize = standardElements.find((e) => e >= effectiveKw) ?? standardElements[standardElements.length - 1]!
  const annualKwh = (totalLitresPerDay * 4.186 * deltaT / 3600) * 365 / efficiencyFactor
  return { totalLitresPerDay, peakLitres, recommendedCapacity, elementKw: elementSize, annualKwh }
}

// ─── Countertop Edge ──────────────────────────────────────────────────────────

const EDGE_PROFILE_MULTIPLIER: Record<string, number> = {
  eased: 1.0, bullnose: 1.3, ogee: 1.8, waterfall: 2.5, mitred: 1.5,
}

export function calcCountertopEdge(
  sections: Array<{ length: number; exposedSides: number }>,
  profile: string, pricePerLinearM: number,
) {
  const multiplier = EDGE_PROFILE_MULTIPLIER[profile] ?? 1.0
  const totalLinearM = sections.reduce((sum, s) => sum + Math.max(0, s.length) * Math.max(1, s.exposedSides), 0)
  const profilingCost = totalLinearM * Math.max(0, pricePerLinearM) * multiplier
  return { totalLinearM, profilingCost, profileMultiplier: multiplier }
}

// ─── Countertop Labour ────────────────────────────────────────────────────────

const MATERIAL_HOURS_PER_M2: Record<string, number> = {
  granite: 1.5, marble: 1.8, quartz: 1.2, caesarstone: 1.2, laminate: 0.6, solid_surface: 1.0, timber: 1.3,
}

export function calcCountertopLabour(
  areaM2: number, material: string, cutoutCount: number, joinCount: number, labourRatePerHour: number,
) {
  const hoursPerM2 = MATERIAL_HOURS_PER_M2[material] ?? 1.2
  const baseHours = Math.max(0, areaM2) * hoursPerM2
  const cutoutHours = Math.max(0, cutoutCount) * 1.5   // per cutout (sink, hob etc)
  const joinHours = Math.max(0, joinCount) * 0.75       // per join/seam
  const totalHours = baseHours + cutoutHours + joinHours
  const labourCost = totalHours * Math.max(0, labourRatePerHour)
  return { baseHours, cutoutHours, joinHours, totalHours, labourCost }
}

// ─── Decking ──────────────────────────────────────────────────────────────────

export function calcDecking(
  lengthM: number, widthM: number, boardWidthMm: number, gapMm: number,
  joistSpacingM: number, deckHeightM: number,
  boardPricePerM: number, joistPricePerM: number, postPriceEach: number, labourRatePerM2: number,
  wastePct: number,
) {
  const area = Math.max(0, lengthM) * Math.max(0, widthM)
  const boardPitch = (Math.max(50, boardWidthMm) + Math.max(0, gapMm)) / 1000
  const boardCount = Math.ceil(Math.max(0, widthM) / boardPitch)
  const boardRunM = boardCount * Math.max(0, lengthM) * (1 + Math.max(0, wastePct) / 100)
  const joistCount = Math.ceil(Math.max(0, lengthM) / Math.max(0.3, joistSpacingM)) + 1
  const joistRunM = joistCount * Math.max(0, widthM) * 1.05  // 5% for cuts
  // Posts: perimeter + interior at 1.8m grid if deck height > 0.3m
  const perimeterPosts = 2 * (Math.ceil(lengthM / 1.8) + Math.ceil(widthM / 1.8))
  const interiorPosts = deckHeightM > 0.3
    ? Math.ceil(lengthM / 1.8) * Math.ceil(widthM / 1.8)
    : 0
  const postCount = perimeterPosts + interiorPosts
  const boardCost = boardRunM * Math.max(0, boardPricePerM)
  const joistCost = joistRunM * Math.max(0, joistPricePerM)
  const postCost = postCount * Math.max(0, postPriceEach)
  const labourCost = area * Math.max(0, labourRatePerM2)
  const totalCost = boardCost + joistCost + postCost + labourCost
  return { areaM2: area, boardCount, boardRunM, joistCount, joistRunM, postCount, boardCost, joistCost, postCost, labourCost, totalCost }
}

// ─── Pressure Washing ─────────────────────────────────────────────────────────

const PRESSURE_WASH_RATE_M2_PER_HOUR: Record<string, number> = {
  concrete_flat: 80, concrete_textured: 50, brick: 40, wood: 45, roof_tile: 35, paving: 70, render: 55,
}
const PRESSURE_WASH_WATER_LPM: Record<string, number> = {
  domestic: 7, commercial: 15, industrial: 25,
}

export function calcPressureWashing(
  areaM2: number, surface: string, equipment: 'domestic' | 'commercial' | 'industrial',
  pricePerM2: number, waterCostPerKl: number,
) {
  const rate = PRESSURE_WASH_RATE_M2_PER_HOUR[surface] ?? 60
  const lpm = PRESSURE_WASH_WATER_LPM[equipment] ?? 15
  const hours = Math.max(0, areaM2) / rate
  const waterUsedKl = (hours * 60 * lpm) / 1000
  const waterCost = waterUsedKl * Math.max(0, waterCostPerKl)
  const serviceCost = Math.max(0, areaM2) * Math.max(0, pricePerM2)
  return { hours, waterUsedKl, waterCost, serviceCost, totalRevenue: serviceCost }
}

// ─── Roof Sheet ───────────────────────────────────────────────────────────────

export function calcRoofSheet(
  ridgeLengthM: number, rafterSpanM: number, pitchDeg: number,
  sheetWidthM: number, overlapM: number, wastePct: number,
  sheetPricePerM: number, labourRatePerM2: number,
) {
  const pitchRad = (Math.max(0, pitchDeg) * Math.PI) / 180
  const rafterLength = Math.max(0, rafterSpanM) / Math.cos(pitchRad)  // slant length
  const roofAreaSlant = Math.max(0, ridgeLengthM) * rafterLength * 2   // both sides
  const effectiveCoverM = Math.max(0.1, sheetWidthM) - Math.max(0, overlapM)
  const sheetsWide = Math.ceil(Math.max(0, ridgeLengthM) / effectiveCoverM)
  const sheetsLong = Math.ceil(rafterLength / 3)  // std sheet length 3m
  const totalSheets = sheetsWide * sheetsLong * (1 + Math.max(0, wastePct) / 100)
  const linearMetres = Math.ceil(totalSheets) * 3
  const materialCost = linearMetres * Math.max(0, sheetPricePerM)
  const labourCost = roofAreaSlant * Math.max(0, labourRatePerM2)
  const totalCost = materialCost + labourCost
  return { rafterLengthM: rafterLength, roofAreaSlant, sheetsWide, sheetsLong, totalSheets: Math.ceil(totalSheets), linearMetres, materialCost, labourCost, totalCost }
}

// ─── Specialist Trade Expansion ──────────────────────────────────────────────

export type QuoteReadyLine = {
  description: string
  quantity: number
  unit: string
  note?: string
}

function pctFactor(pct: number) {
  return 1 + Math.max(0, pct) / 100
}

function roundUpTo(value: number, increment: number) {
  if (increment <= 0) return value
  return Math.ceil(Math.max(0, value) / increment) * increment
}

// ─── Epoxy ───────────────────────────────────────────────────────────────────

export type EpoxySystemType =
  | 'self_leveling_floor'
  | 'thin_roll_coat'
  | 'flake_floor'
  | 'countertop_bar_top'
  | 'repair_patch'
  | 'industrial_high_build'
  | 'casting'

export interface EpoxyFloorPourInputs {
  areaM2: number
  thicknessMm: number
  wastePct: number
  kitVolumeL: number
  epoxySystemType: EpoxySystemType
  densityKgPerL?: number
  coverageRateM2PerL?: number
  primerEnabled?: boolean
  primerCoverageM2PerL?: number
  topcoatEnabled?: boolean
  topcoatCoverageM2PerL?: number
  kitCost?: number
  primerCostPerL?: number
  topcoatCostPerL?: number
}

export function calcEpoxyFloorPour(inputs: EpoxyFloorPourInputs) {
  const areaM2 = Math.max(0, inputs.areaM2)
  const wasteFactor = pctFactor(inputs.wastePct)
  const thicknessVolumeL = areaM2 * Math.max(0, inputs.thicknessMm)
  const coverageVolumeL = inputs.coverageRateM2PerL && inputs.coverageRateM2PerL > 0
    ? areaM2 / inputs.coverageRateM2PerL
    : 0
  const rawProductVolumeL = Math.max(thicknessVolumeL, coverageVolumeL)
  const productVolumeL = rawProductVolumeL * wasteFactor
  const kitSizeL = Math.max(0.1, inputs.kitVolumeL)
  const kitCount = Math.ceil(productVolumeL / kitSizeL)
  const densityKgPerL = Math.max(0, inputs.densityKgPerL ?? 1.1)
  const productWeightKg = productVolumeL * densityKgPerL
  const primerLitres = inputs.primerEnabled && (inputs.primerCoverageM2PerL ?? 0) > 0
    ? (areaM2 / Math.max(0.1, inputs.primerCoverageM2PerL ?? 1)) * wasteFactor
    : 0
  const topcoatLitres = inputs.topcoatEnabled && (inputs.topcoatCoverageM2PerL ?? 0) > 0
    ? (areaM2 / Math.max(0.1, inputs.topcoatCoverageM2PerL ?? 1)) * wasteFactor
    : 0
  const materialCost =
    kitCount * Math.max(0, inputs.kitCost ?? 0) +
    primerLitres * Math.max(0, inputs.primerCostPerL ?? 0) +
    topcoatLitres * Math.max(0, inputs.topcoatCostPerL ?? 0)

  const quoteReadyLines: QuoteReadyLine[] = [
    {
      description: `Epoxy ${inputs.epoxySystemType.replace(/_/g, ' ')} kits`,
      quantity: kitCount,
      unit: 'kit',
      note: `${kitSizeL}L kit, ${inputs.thicknessMm}mm target thickness`,
    },
    ...(primerLitres > 0
      ? [{ description: 'Epoxy primer allowance', quantity: roundUpTo(primerLitres, 0.5), unit: 'L' }]
      : []),
    ...(topcoatLitres > 0
      ? [{ description: 'Epoxy topcoat allowance', quantity: roundUpTo(topcoatLitres, 0.5), unit: 'L' }]
      : []),
  ]

  return {
    areaM2,
    rawProductVolumeL,
    productVolumeL,
    productWeightKg,
    kitCount,
    primerLitres,
    topcoatLitres,
    materialCost,
    quoteReadyLines,
  }
}

export interface EpoxyMixRatioInputs {
  resinParts: number
  hardenerParts: number
  targetBatchSize: number
  splitBatches: number
  unit: 'L' | 'kg' | 'ml' | 'g'
}

export function calcEpoxyMixRatio(inputs: EpoxyMixRatioInputs) {
  const resinParts = Math.max(0, inputs.resinParts)
  const hardenerParts = Math.max(0, inputs.hardenerParts)
  const totalParts = resinParts + hardenerParts
  const batchSize = Math.max(0, inputs.targetBatchSize)
  const resinAmount = totalParts > 0 ? batchSize * (resinParts / totalParts) : 0
  const hardenerAmount = totalParts > 0 ? batchSize * (hardenerParts / totalParts) : 0
  const splitCount = Math.max(1, Math.floor(inputs.splitBatches))
  const splitBatchSize = batchSize / splitCount
  const splitResinAmount = resinAmount / splitCount
  const splitHardenerAmount = hardenerAmount / splitCount
  const largeBatchWarning =
    ((inputs.unit === 'L' || inputs.unit === 'kg') && batchSize > 10) ||
    ((inputs.unit === 'ml' || inputs.unit === 'g') && batchSize > 10000)

  return {
    ratioSummary: `${resinParts}:${hardenerParts}`,
    totalParts,
    resinAmount,
    hardenerAmount,
    splitCount,
    splitBatchSize,
    splitResinAmount,
    splitHardenerAmount,
    largeBatchWarning,
  }
}

export type EpoxyRecommendationInput = {
  surfaceType: 'floor' | 'countertop' | 'repair' | 'casting' | 'wall' | 'other'
  location: 'indoor' | 'outdoor' | 'mixed'
  uvExposure: 'low' | 'medium' | 'high'
  trafficLevel: 'light' | 'medium' | 'heavy' | 'industrial'
  finishIntent: 'decorative' | 'industrial' | 'clear' | 'repair'
  chemicalResistance: boolean
  thicknessMm: number
  substrateCondition: 'sound' | 'porous' | 'cracked' | 'damp' | 'unknown'
}

export function recommendEpoxySystem(input: EpoxyRecommendationInput) {
  let category: EpoxySystemType = 'thin_roll_coat'
  if (input.surfaceType === 'countertop') category = 'countertop_bar_top'
  else if (input.surfaceType === 'casting') category = 'casting'
  else if (input.surfaceType === 'repair') category = 'repair_patch'
  else if (input.trafficLevel === 'industrial' || input.chemicalResistance || input.thicknessMm >= 2.5) category = 'industrial_high_build'
  else if (input.finishIntent === 'decorative') category = 'flake_floor'
  else if (input.surfaceType === 'floor' && input.thicknessMm >= 1.5) category = 'self_leveling_floor'

  const cautions = [
    input.uvExposure === 'high' ? 'Use a UV-stable topcoat or confirm yellowing risk before quoting.' : null,
    input.substrateCondition === 'damp' ? 'Check moisture and vapour tolerance before any epoxy system is committed.' : null,
    input.substrateCondition === 'cracked' ? 'Repair cracks and movement joints before final quantities are priced.' : null,
    input.substrateCondition === 'porous' ? 'Allow primer/sealer coat and confirm absorption on site.' : null,
    input.location === 'outdoor' ? 'Outdoor exposure may need polyurethane, polyaspartic, or UV-stable protection over epoxy.' : null,
  ].filter((entry): entry is string => Boolean(entry))

  const nextCalculator =
    category === 'countertop_bar_top' ? 'concrete-countertop-volume' :
    category === 'casting' || category === 'repair_patch' ? 'epoxy-mix-ratio' :
    'epoxy-floor-pour'

  return {
    recommendedCategory: category,
    summary: `${category.replace(/_/g, ' ')} is the best starting category for this set of site conditions.`,
    cautions,
    nextCalculator,
  }
}

export function calcEpoxyCoverageByThickness(kitVolumeL: number, thicknessMm: number, areaM2: number, wastePct: number) {
  const kit = Math.max(0, kitVolumeL)
  const thickness = Math.max(0.1, thicknessMm)
  const wasteFactor = pctFactor(wastePct)
  const coverageAreaM2 = kit / thickness
  const totalVolumeNeededL = Math.max(0, areaM2) * thickness * wasteFactor
  const kitCount = Math.ceil(totalVolumeNeededL / Math.max(0.1, kit))
  const thicknessOptions = [0.5, 1, 2, 3, 4].map((optionMm) => ({
    thicknessMm: optionMm,
    coverageM2: kit / optionMm,
    kitsForArea: Math.ceil((Math.max(0, areaM2) * optionMm * wasteFactor) / Math.max(0.1, kit)),
  }))
  return { coverageAreaM2, totalVolumeNeededL, kitCount, thicknessOptions }
}

// ─── Concrete Countertops ────────────────────────────────────────────────────

export type CountertopVolumeSection = { id?: string | number; label?: string; lengthM: number; depthM: number }
export type CountertopCutout = { id?: string | number; label?: string; lengthM: number; widthM: number }

export interface ConcreteCountertopVolumeInputs {
  sections: CountertopVolumeSection[]
  thicknessMm: number
  cutouts: CountertopCutout[]
  backsplashEnabled: boolean
  backsplashHeightMm: number
  backsplashThicknessMm: number
  edgeAllowancePct: number
  wastePct: number
  densityKgPerM3?: number
}

export function calcConcreteCountertopVolume(inputs: ConcreteCountertopVolumeInputs) {
  const thicknessM = Math.max(0, inputs.thicknessMm) / 1000
  const grossAreaM2 = inputs.sections.reduce(
    (sum, section) => sum + Math.max(0, section.lengthM) * Math.max(0, section.depthM),
    0,
  )
  const cutoutAreaM2 = inputs.cutouts.reduce(
    (sum, cutout) => sum + Math.max(0, cutout.lengthM) * Math.max(0, cutout.widthM),
    0,
  )
  const netAreaM2 = Math.max(0, grossAreaM2 - cutoutAreaM2)
  const deckVolumeM3 = netAreaM2 * thicknessM
  const totalRunLengthM = inputs.sections.reduce((sum, section) => sum + Math.max(0, section.lengthM), 0)
  const backsplashVolumeM3 = inputs.backsplashEnabled
    ? totalRunLengthM * (Math.max(0, inputs.backsplashHeightMm) / 1000) * (Math.max(0, inputs.backsplashThicknessMm) / 1000)
    : 0
  const allowanceVolumeM3 = (deckVolumeM3 + backsplashVolumeM3) * (Math.max(0, inputs.edgeAllowancePct) / 100)
  const rawVolumeM3 = deckVolumeM3 + backsplashVolumeM3 + allowanceVolumeM3
  const orderVolumeM3 = rawVolumeM3 * pctFactor(inputs.wastePct)
  const litres = orderVolumeM3 * 1000
  const weightKg = orderVolumeM3 * Math.max(0, inputs.densityKgPerM3 ?? 2350)
  const reinforcementLm = totalRunLengthM * 2 + netAreaM2 * 2

  return {
    grossAreaM2,
    cutoutAreaM2,
    netAreaM2,
    totalRunLengthM,
    backsplashVolumeM3,
    rawVolumeM3,
    orderVolumeM3,
    litres,
    weightKg,
    reinforcementLm,
    quoteReadyLines: [
      { description: 'Concrete countertop mix volume', quantity: roundUpTo(litres, 5), unit: 'L' },
      { description: 'Countertop reinforcement starter allowance', quantity: roundUpTo(reinforcementLm, 1), unit: 'lm' },
    ] satisfies QuoteReadyLine[],
  }
}

export type CountertopMixPreset = 'bagged_countertop_mix' | 'sand_cement_aggregate' | 'gf_rc'

export function calcConcreteCountertopMaterials(
  volumeM3: number,
  mixPreset: CountertopMixPreset,
  wastePct: number,
  bagSizeKg: number,
  includePigment: boolean,
  includeAdditive: boolean,
  sealerCoverageM2PerL: number,
  exposedAreaM2: number,
) {
  const orderVolumeM3 = Math.max(0, volumeM3) * pctFactor(wastePct)
  const bagSize = Math.max(1, bagSizeKg)
  const dryDensityKgPerM3 = mixPreset === 'gf_rc' ? 2050 : mixPreset === 'bagged_countertop_mix' ? 2200 : 2250
  const totalDryKg = orderVolumeM3 * dryDensityKgPerM3
  const bagCount = Math.ceil(totalDryKg / bagSize)
  const cementKg = mixPreset === 'sand_cement_aggregate' ? totalDryKg * 0.22 : mixPreset === 'gf_rc' ? totalDryKg * 0.28 : totalDryKg
  const sandKg = mixPreset === 'sand_cement_aggregate' ? totalDryKg * 0.38 : 0
  const aggregateKg = mixPreset === 'sand_cement_aggregate' ? totalDryKg * 0.40 : 0
  const pigmentKg = includePigment ? cementKg * 0.03 : 0
  const additiveAllowanceKg = includeAdditive ? totalDryKg * 0.01 : 0
  const sealerLitres = sealerCoverageM2PerL > 0
    ? Math.max(0, exposedAreaM2) / sealerCoverageM2PerL * 2
    : 0

  return {
    orderVolumeM3,
    totalDryKg,
    bagCount,
    cementKg,
    sandKg,
    aggregateKg,
    pigmentKg,
    additiveAllowanceKg,
    sealerLitres,
    quoteReadyLines: [
      { description: mixPreset.replace(/_/g, ' '), quantity: bagCount, unit: `${bagSize}kg bag` },
      ...(pigmentKg > 0 ? [{ description: 'Pigment allowance', quantity: roundUpTo(pigmentKg, 0.1), unit: 'kg' }] : []),
      ...(additiveAllowanceKg > 0 ? [{ description: 'Concrete additive allowance', quantity: roundUpTo(additiveAllowanceKg, 0.1), unit: 'kg' }] : []),
      ...(sealerLitres > 0 ? [{ description: 'Countertop sealer allowance', quantity: roundUpTo(sealerLitres, 0.5), unit: 'L' }] : []),
    ] satisfies QuoteReadyLine[],
  }
}

// ─── Cabinet / Kitchen Layout ────────────────────────────────────────────────

export type CabinetWallRun = { id?: string | number; label?: string; lengthM: number }
export type CabinetVoid = { id?: string | number; label?: string; widthM: number }

export function calcCabinetLayout(
  wallRuns: CabinetWallRun[],
  applianceVoids: CabinetVoid[],
  moduleWidthsMm: number[],
  includeTallUnit: boolean,
  includeIsland: boolean,
  includeEndPanelsFillers: boolean,
) {
  const moduleWidths = moduleWidthsMm.filter((width) => width > 0).sort((a, b) => b - a)
  const totalWallLengthM = wallRuns.reduce((sum, run) => sum + Math.max(0, run.lengthM), 0)
  const voidLengthM = applianceVoids.reduce((sum, item) => sum + Math.max(0, item.widthM), 0)
  const tallUnitAllowanceM = includeTallUnit ? 0.6 : 0
  const availableLengthM = Math.max(0, totalWallLengthM - voidLengthM - tallUnitAllowanceM)
  const modules: number[] = []
  let remainingMm = availableLengthM * 1000
  for (const width of moduleWidths) {
    while (remainingMm >= width - 20) {
      modules.push(width)
      remainingMm -= width
    }
  }
  const fillerAllowanceM = includeEndPanelsFillers ? Math.max(0.08, remainingMm / 1000 + modules.length * 0.015) : Math.max(0, remainingMm / 1000)
  const baseCabinetCount = modules.length + (includeTallUnit ? 1 : 0)
  const wallCabinetCount = Math.max(0, Math.round(modules.length * 0.75))
  const islandLengthM = includeIsland ? 1.8 : 0
  const worktopLengthM = availableLengthM + islandLengthM

  return {
    totalWallLengthM,
    voidLengthM,
    availableLengthM,
    moduleWidths: modules,
    baseCabinetCount,
    wallCabinetCount,
    tallUnitCount: includeTallUnit ? 1 : 0,
    islandLengthM,
    worktopLengthM,
    fillerAllowanceM,
    quoteReadyLines: [
      { description: 'Base cabinet modules', quantity: baseCabinetCount, unit: 'unit' },
      { description: 'Wall cabinet modules', quantity: wallCabinetCount, unit: 'unit' },
      { description: 'Worktop run allowance', quantity: roundUpTo(worktopLengthM, 0.1), unit: 'lm' },
      { description: 'Fillers/end panels allowance', quantity: roundUpTo(fillerAllowanceM, 0.1), unit: 'lm' },
    ] satisfies QuoteReadyLine[],
  }
}

export type HardwareAllowanceMode = 'basic' | 'standard' | 'heavy_duty'

export function calcCabinetMaterials(
  cabinetCount: number,
  doorCount: number,
  drawerCount: number,
  shelfCount: number,
  sheetAreaM2: number,
  boardAreaPerCabinetM2: number,
  includeKickboard: boolean,
  hardwareAllowanceMode: HardwareAllowanceMode,
  labourRatePerHour: number,
) {
  const safeCabinets = Math.max(0, cabinetCount)
  const boardAreaM2 =
    safeCabinets * Math.max(0, boardAreaPerCabinetM2) +
    Math.max(0, doorCount) * 0.42 +
    Math.max(0, shelfCount) * 0.35
  const sheetCount = Math.ceil(boardAreaM2 / Math.max(0.1, sheetAreaM2))
  const edgingLm = safeCabinets * 5.5 + Math.max(0, doorCount) * 2.6 + Math.max(0, shelfCount) * 1.2
  const hingeCount = Math.max(0, doorCount) * 2
  const runnerCount = Math.max(0, drawerCount)
  const handleCount = Math.max(0, doorCount) + Math.max(0, drawerCount)
  const kickboardLm = includeKickboard ? safeCabinets * 0.6 : 0
  const hardwareMultiplier = hardwareAllowanceMode === 'heavy_duty' ? 1.25 : hardwareAllowanceMode === 'standard' ? 1.1 : 1
  const labourHours = (safeCabinets * 1.4 + Math.max(0, drawerCount) * 0.6 + Math.max(0, doorCount) * 0.25) * hardwareMultiplier
  const labourCost = labourHours * Math.max(0, labourRatePerHour)

  return {
    boardAreaM2,
    sheetCount,
    edgingLm,
    hingeCount,
    runnerCount,
    handleCount,
    kickboardLm,
    labourHours,
    labourCost,
    quoteReadyLines: [
      { description: 'Cabinet board sheets', quantity: sheetCount, unit: 'sheet' },
      { description: 'Edging allowance', quantity: roundUpTo(edgingLm, 1), unit: 'lm' },
      { description: 'Hinges', quantity: hingeCount, unit: 'each' },
      { description: 'Drawer runners', quantity: runnerCount, unit: 'pair' },
      { description: 'Handles', quantity: handleCount, unit: 'each' },
      ...(kickboardLm > 0 ? [{ description: 'Kickboard/plinth allowance', quantity: roundUpTo(kickboardLm, 0.1), unit: 'lm' }] : []),
    ] satisfies QuoteReadyLine[],
  }
}

// ─── Floorplan Measurement Model ─────────────────────────────────────────────

export type FloorplanPoint = { id: string; x: number; y: number }
export type FloorplanFinishTag =
  | 'paint'
  | 'tile'
  | 'waterproof'
  | 'cabinetry'
  | 'countertop'
  | 'flooring'
  | 'epoxy'
  | 'drywall_partition'
  | 'plumbing_wall'
  | 'electrical_wall'
  | 'other'

export type FloorplanProgressState = 'not_started' | 'in_progress' | 'complete' | 'snag' | 'awaiting_approval'

export type FloorplanAttachment = {
  id: string
  label: string
  kind: 'photo' | 'note' | 'voice_note'
  note?: string
  fileName?: string
  targetType: 'room' | 'wall' | 'zone' | 'fixture'
  targetId: string
  createdAt: string
}

export type FloorplanWallSegment = {
  id: string
  startId: string
  endId: string
  lengthM?: number
  finishTag?: FloorplanFinishTag | null
  progressState?: FloorplanProgressState | null
  notes?: string | null
  linkedTaskIds?: string[]
}
export type FloorplanFixture = {
  id: string
  kind: string
  label: string
  x: number
  y: number
  widthM?: number
  depthM?: number
  wallId?: string | null
  positionOnWallM?: number | null
  /** Rotation in degrees, snapped to 0/90/180/270. Undefined treated as 0. */
  rotation?: number
  /** For doors: which jamb the hinge sits on. Defaults to 'start'. */
  hingeSide?: 'start' | 'end' | null
  /** For doors: which side of the wall the leaf swings to. Defaults to 'inward'. */
  swingDirection?: 'inward' | 'outward' | null
  /** Door style. 'swing' (default) shows a leaf + arc; 'sliding' shows a slider track. */
  doorVariant?: 'swing' | 'sliding' | null
}

export type FloorplanAnnotation = {
  id: string
  kind: 'note' | 'arrow' | 'callout'
  text: string
  x: number
  y: number
  /** Optional arrow endpoint (for kind='arrow' or 'callout'). */
  targetX?: number | null
  targetY?: number | null
  /** Optional rotation of the label box (0/90/180/270). */
  rotation?: number
}

/**
 * Optional ad-hoc measurement line drawn between two world-space points.
 * Used for diagonal measurements, custom dimensions, and other reference lengths
 * that are not implied by the wall structure.
 */
export type FloorplanMeasurement = {
  id: string
  startX: number
  startY: number
  endX: number
  endY: number
  /** Optional user label shown next to the length. */
  label?: string | null
}

export type FloorplanZone = {
  id: string
  label: string
  areaM2: number
  kind: 'tile' | 'paint' | 'waterproofing' | 'floor_finish' | 'other'
  finishTag?: FloorplanFinishTag | null
  progressState?: FloorplanProgressState | null
  notes?: string | null
  linkedTaskIds?: string[]
}

export type FloorplanArea = {
  id: string
  label: string
  type?: string | null
  progressState?: FloorplanProgressState | null
  notes?: string | null
  closed?: boolean
  points: FloorplanPoint[]
  walls: FloorplanWallSegment[]
  fixtures: FloorplanFixture[]
  zones: FloorplanZone[]
  attachments?: FloorplanAttachment[]
  annotations?: FloorplanAnnotation[]
}

export type FloorplanModel = {
  id: string
  orgId?: string | null
  linkedJobId?: string | null
  linkedNotebookId?: string | null
  linkedQuoteId?: string | null
  name: string
  roomLabel?: string | null
  roomType?: string | null
  roomProgressState?: FloorplanProgressState | null
  roomNotes?: string | null
  activeAreaId?: string | null
  areas?: FloorplanArea[]
  closed?: boolean
  points: FloorplanPoint[]
  walls: FloorplanWallSegment[]
  fixtures: FloorplanFixture[]
  zones: FloorplanZone[]
  attachments?: FloorplanAttachment[]
  annotations?: FloorplanAnnotation[]
  measurements?: FloorplanMeasurement[]
  updatedAt?: string
}

function distanceBetweenPoints(a: FloorplanPoint, b: FloorplanPoint) {
  return Math.hypot(b.x - a.x, b.y - a.y)
}

export function calculateFloorplanMetrics(plan: FloorplanModel) {
  const pointMap = new Map(plan.points.map((point) => [point.id, point]))
  const wallLengths = plan.walls.map((wall) => {
    const start = pointMap.get(wall.startId)
    const end = pointMap.get(wall.endId)
    const fallback = start && end ? distanceBetweenPoints(start, end) : 0
    return {
      id: wall.id,
      lengthM: Math.max(0, wall.lengthM ?? fallback),
    }
  })
  const perimeterM = wallLengths.reduce((sum, wall) => sum + wall.lengthM, 0)
  const closed = plan.closed ?? (plan.points.length >= 3 && plan.walls.length >= plan.points.length)
  const areaFromGeometry = closed
    ? Math.abs(plan.points.reduce((sum, point, index) => {
        const next = plan.points[(index + 1) % plan.points.length]
        return sum + point.x * next.y - next.x * point.y
      }, 0)) / 2
    : 0
  const openingCount = plan.fixtures.filter((fixture) => (
    fixture.kind === 'door' || fixture.kind === 'sliding-door' || fixture.kind === 'window' || fixture.kind === 'opening'
  )).length
  const cabinetRunLengthM = plan.fixtures
    .filter((fixture) => fixture.kind === 'cabinet' || fixture.kind === 'countertop')
    .reduce((sum, fixture) => sum + Math.max(0, fixture.widthM ?? 0), 0)
  const zoneAreas = plan.zones.reduce<Record<string, number>>((acc, zone) => {
    const key = zone.finishTag ?? zone.kind
    acc[key] = (acc[key] ?? 0) + Math.max(0, zone.areaM2)
    return acc
  }, {})
  const wallRunByFinish = plan.walls.reduce<Record<string, number>>((acc, wall, index) => {
    const tag = wall.finishTag ?? 'untagged'
    acc[tag] = (acc[tag] ?? 0) + (wallLengths[index]?.lengthM ?? 0)
    return acc
  }, {})
  const progressCounts = [
    plan.roomProgressState,
    ...plan.walls.map((wall) => wall.progressState),
    ...plan.zones.map((zone) => zone.progressState),
  ].reduce<Record<string, number>>((acc, state) => {
    const key = state ?? 'not_started'
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})

  return {
    perimeterM,
    areaM2: areaFromGeometry,
    wallLengths,
    openingCount,
    fixtureCount: plan.fixtures.length,
    fixtureList: plan.fixtures.map((fixture) => fixture.label),
    cabinetRunLengthM,
    zoneAreas,
    wallRunByFinish,
    progressCounts,
  }
}
